import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useReducedMotion } from 'framer-motion';
import { DockNote } from './DockNote';
import { ThinkingDots } from './ThinkingDots';
import { QuestFinaleNotesMobile } from './QuestFinaleNotesMobile';
import { useQuest } from '../hooks/useQuest';
import { useIsMobile } from '../hooks/useIsMobile';
import { LIFTOFF_S } from '../hooks/useQuestPilot';
import { quest } from '../utils/quest';
import { CHEER_DUR, type CheerKind } from '../utils/questCheer';
import { scriptedScroll } from '../utils/pixelActions';
import { QTE_HITS, finaleAsk, finaleGoodbye, finaleRemember, finaleStory, type FinaleLine } from '../data/questFinaleScript';
import type { ChatPhase } from '../types/chat';
import type { Emotion } from '../types/emotion';
interface QuestFinaleProps {
  onPhase: (phase: ChatPhase, emotion: Emotion) => void;
  onPulse: () => void;
}
type Beat = 'intro' | 'qte' | 'cheer' | 'remember' | 'story' | 'goodbye' | 'charge' | 'rise' | 'gone';
interface Note {
  id: number;
  text: string;
}
const MAX_STACK = 4;
/** His interface red — the lift-off exhaust is all shades of it */
const REDS = ['#FF3424', '#FF5A4D', '#E5200F', '#FF7A6E', '#C8170A'];
/** Timing game: sweeps per second and target-zone width, per round */
const QTE_SPEED = [0.5, 0.68, 0.86, 1.08];
const QTE_ZONE = [0.26, 0.21, 0.17, 0.14];
/** Misses in a row before the timing game starts over */
const QTE_MAX_MISSES = 3;
/** How long the hit / miss feedback plays on his face (ms) */
const FLASH_MS = 700;
/** Flight from the corner into the middle of the footer (ms) */
const FLY_MS = 1700;
/** After lift-off the rumble dies away over this long (ms) */
const RUMBLE_FADE_MS = 700;
/** Keys that would scroll the page (blocked during lift-off) */
const SCROLL_KEYS = new Set([' ', 'Spacebar', 'PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', 'Tab']);

/**
 * Restoring Pixel's memory, and his trip home. No overlay, no buttons. It all happens in the middle
 * of the footer (pinned to the page), and he talks to you right where he is:
 *  1. Fifth shard found → he flies out of the corner into the footer, celebrates, then explains.
 *  2. Timing game on his face — click him while the bar is between the posts, 4×.
 *     Won → he celebrates: memory restored.
 *  3. He tells you what he remembers and who launched him, then asks you to charge him.
 *  4. Take-off charge: his battery screen — the very same one as the battery easter egg. Click to
 *     fill it; the whole page rumbles harder the fuller it gets.
 *  5. Full → he lifts off at once: the page rushes back up to the top, faster and faster, red exhaust
 *     pouring out of him, and he shoots off the top. He's gone afterwards.
 */
export function QuestFinale({
  onPhase,
  onPulse
}: QuestFinaleProps) {
  const state = useQuest();
  const reduce = !!useReducedMotion();
  const isMobile = useIsMobile();
  const on = state.status === 'active' && (state.step === 'restore' || state.step === 'finale');
  const [beat, setBeat] = useState<Beat>('intro');
  const beatRef = useRef<Beat>('intro');
  beatRef.current = beat;
  const [notes, setNotes] = useState<Note[]>([]);
  const [thinking, setThinking] = useState(false);
  const run = useRef(0);
  const idRef = useRef(0);
  const qte = useRef({
    round: 0,
    phase: 0,
    flashUntil: 0,
    flashStart: 0,
    misses: 0
  });
  const cb = useRef({
    onPhase,
    onPulse
  });
  cb.current = {
    onPhase,
    onPulse
  };

  /** Say lines one after another, next to him. Resolves false if interrupted. */
  const say = useCallback(async (lines: FinaleLine[]) => {
    const my = ++run.current;
    const sleep = (ms: number) => new Promise<boolean>(r => setTimeout(() => r(run.current === my), ms));
    setNotes([]);
    for (const line of lines) {
      setThinking(true);
      cb.current.onPhase('thinking', 'curious');
      if (!(await sleep(reduce ? 120 : 360 + Math.min(line.text.length * 10, 500)))) return false;
      setThinking(false);
      setNotes(n => [...n, {
        id: ++idRef.current,
        text: line.text
      }].slice(-MAX_STACK));
      cb.current.onPhase('speaking', line.emotion);
      if (!(await sleep(reduce ? 400 : 900 + Math.min(line.text.length * 26, 1500)))) return false;
    }
    setThinking(false);
    cb.current.onPhase('listening', 'curious');
    return true;
  }, [reduce]);
  const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

  /** A celebration on the spot: 'found' (all five shards) or 'won' (timing game beaten) */
  const celebrate = async (kind: CheerKind) => {
    setNotes([]);
    quest.setFinaleStage('celebrate');
    quest.cheer(kind);
    cb.current.onPhase('reacting', 'happy');
    await sleep(reduce ? 300 : CHEER_DUR[kind] * 1000 + 150);
  };

  /** Fresh timing game on his face */
  const startQte = () => {
    qte.current = {
      round: 0,
      phase: 0,
      flashUntil: 0,
      flashStart: 0,
      misses: 0
    };
    Object.assign(quest.qteGlyph, {
      icon: 'slider',
      text: '',
      value: 0,
      dir: 1,
      zone: QTE_ZONE[0],
      hits: 0,
      need: QTE_HITS,
      flash: null,
      flashAge: 1,
      still: true
    });
    setNotes([]);
    quest.setFinaleStage('qte');
    setBeat('qte');
  };

  // While the restoration runs: no text selection or focus outlines from all that rapid clicking
  useEffect(() => {
    if (!on) return;
    const root = document.documentElement;
    root.classList.add('quest-finale');
    window.getSelection()?.removeAllRanges();
    return () => root.classList.remove('quest-finale');
  }, [on]);

  // Enter: all five found → straight out of the corner into the footer
  useEffect(() => {
    if (!on) {
      run.current++;
      quest.finaleInput = null;
      setRumble(null);
      setNotes([]);
      setThinking(false);
      return;
    }
    if (state.step !== 'restore') return;
    setBeat('intro');
    quest.restore(); // → finale, flying to the middle of the footer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, state.step]);

  // The battery reporting full (→ lift-off at once), and testing jumps
  useEffect(() => {
    if (!on) return;
    if (state.event === 'charged' && beatRef.current === 'charge') {
      setBeat('rise');
      return;
    }
    if (state.event !== 'jump') return;
    const target = quest.finaleJump;
    run.current++; // interrupt whatever he was saying
    setNotes([]);
    setThinking(false);
    if (target === 'qte') startQte();else if (target === 'story') setBeat('story');else if (target === 'charge') setBeat('charge');else if (target === 'launch') setBeat('rise');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tick]);

  // Beat flow
  useEffect(() => {
    if (!on) return;
    let alive = true;
    const go = async () => {
      if (beat === 'intro') {
        if (state.step !== 'finale') return;
        // Lands in the footer, spin-launches into a backflip and takes a bow, then tells you the plan
        await sleep(reduce ? 200 : FLY_MS);
        if (!alive) return;
        await celebrate('found');
        if (!alive) return;
        quest.setFinaleStage('talk');
        if ((await say(finaleAsk)) && alive) startQte();
      } else if (beat === 'cheer') {
        // Won the timing game: memory restored — his face stops glitching right here
        quest.stopGlitching();
        await celebrate('won');
        if (alive) setBeat('remember');
      } else if (beat === 'remember') {
        // Stays right where he is and talks to you
        quest.setFinaleStage('talk');
        if ((await say(finaleRemember)) && alive) setBeat('story');
      } else if (beat === 'story') {
        quest.setFinaleStage('talk');
        if ((await say(finaleStory)) && alive) setBeat('goodbye');
      } else if (beat === 'goodbye') {
        quest.setFinaleStage('talk');
        if ((await say(finaleGoodbye)) && alive) setBeat('charge');
      } else if (beat === 'charge') {
        // His battery screen comes up; the 3D scene reports back once you've filled it
        setNotes([]);
        quest.setFinaleStage('charge');
      } else if (beat === 'rise') {
        // Lift-off, straight from where he is: the page rushes back up to the top, faster and faster,
        // while he climbs and shoots off; the rumble dies away as he leaves the ground
        quest.markLiftOff();
        quest.setFinaleStage('rise');
        // Chattering away with joy the whole way up
        cb.current.onPhase('speaking', 'happy');
        scriptedScroll({
          y: 0,
          duration: LIFTOFF_S,
          accelerate: true
        });
        if (!reduce) {
          const from = Number(document.documentElement.style.getPropertyValue('--quest-rumble')) || 1;
          await rampRumble(from, 0, RUMBLE_FADE_MS, () => true);
        }
        setRumble(null);
        await sleep(reduce ? 300 : LIFTOFF_S * 1000 - RUMBLE_FADE_MS + 400);
        if (alive) setBeat('gone');
      } else if (beat === 'gone') {
        quest.complete();
        // He's gone: a 2-second beat of silence, then the whole site starts fresh, from the top
        await sleep(2000);
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
        window.location.reload();
      }
    };
    go();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat, on, state.step]);

  // Timing game, driven every frame (written straight onto his face)
  useEffect(() => {
    if (beat !== 'qte') return;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const q = qte.current;
      const g = quest.qteGlyph;
      q.phase += dt * QTE_SPEED[Math.min(q.round, QTE_SPEED.length - 1)];
      // Smooth pendulum: eases into each turn at the ends, fastest through the middle
      const a = q.phase * Math.PI * 2;
      g.value = 0.5 - 0.5 * Math.cos(a);
      g.dir = Math.sin(a) >= 0 ? 1 : -1;
      g.zone = QTE_ZONE[Math.min(q.round, QTE_ZONE.length - 1)];
      g.hits = q.round;
      g.flashAge = (now - q.flashStart) / FLASH_MS;
      if (now > q.flashUntil) {
        g.text = '';
        g.flash = null;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [beat]);

  // Lift-off: the scripted rush to the top can't be interrupted — wheel, touch, keyboard scrolling
  // and clicks are all swallowed until he's gone
  useEffect(() => {
    if (beat !== 'rise') return;
    const stop = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onKey = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) stop(e);
    };
    const opts: AddEventListenerOptions = {
      passive: false,
      capture: true
    };
    window.addEventListener('wheel', stop, opts);
    window.addEventListener('touchstart', stop, opts);
    window.addEventListener('touchmove', stop, opts);
    window.addEventListener('pointerdown', stop, opts);
    window.addEventListener('mousedown', stop, opts);
    window.addEventListener('keydown', onKey, opts);
    const root = document.documentElement;
    root.classList.add('quest-liftoff');
    return () => {
      window.removeEventListener('wheel', stop, opts);
      window.removeEventListener('touchstart', stop, opts);
      window.removeEventListener('touchmove', stop, opts);
      window.removeEventListener('pointerdown', stop, opts);
      window.removeEventListener('mousedown', stop, opts);
      window.removeEventListener('keydown', onKey, opts);
      root.classList.remove('quest-liftoff');
    };
  }, [beat]);

  // Take-off charge: the whole page rumbles while you click, harder the fuller his battery gets
  useEffect(() => {
    if (beat !== 'charge' || reduce) return;
    let raf = 0;
    const loop = () => {
      // Barely there at 10%, a proper earthquake at 100%
      const k = Math.max(0, Math.min(1, (quest.batteryLevel - 0.1) / 0.9));
      setRumble(k > 0.02 ? 0.15 + 0.85 * k * k : null);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    // Lift-off takes the rumble over from here (it fades it out), so it isn't cleared on the way out
    return () => cancelAnimationFrame(raf);
  }, [beat, reduce]);

  // Touching Pixel — the only control
  useEffect(() => {
    if (!on) return;
    quest.finaleInput = (down, onPixel) => {
      if (!down || !onPixel) return;
      const b = beatRef.current;
      const q = qte.current;
      const g = quest.qteGlyph;
      if (b === 'qte') {
        if (q.round >= QTE_HITS) return; // done, celebrating
        const now = performance.now();
        const off = Math.abs((g.value ?? 0.5) * 2 - 1);
        const hit = off <= (g.zone ?? 0.2) + 0.03;
        q.flashStart = now;
        q.flashUntil = now + FLASH_MS;
        g.flashAge = 0;
        if (hit) {
          q.round += 1;
          q.misses = 0;
          g.hits = q.round;
          g.flash = 'hit';
          g.text = 'NICE!';
          cb.current.onPhase('reacting', 'happy');
          if (q.round >= QTE_HITS) {
            g.text = 'WHOA!';
            window.setTimeout(() => setBeat('cheer'), 700);
          }
        } else {
          q.misses += 1;
          g.flash = 'miss';
          if (q.misses >= QTE_MAX_MISSES) {
            // Too many misses: the whole timing game starts over
            q.round = 0;
            q.misses = 0;
            q.phase = 0;
            g.hits = 0;
            g.text = 'AGAIN';
            q.flashUntil = now + FLASH_MS * 1.6;
            cb.current.onPhase('reacting', 'sad');
          } else {
            g.text = 'MISS';
            cb.current.onPhase('reacting', 'surprised');
          }
        }
      }
      // (The take-off charge is handled by the 3D scene itself — the same battery as the easter egg)
    };
    return () => {
      quest.finaleInput = null;
    };
  }, [on]);

  // Leaving mid-restoration (e.g. page change) cleans up
  useEffect(() => () => {
    quest.finaleInput = null;
    setRumble(null);
  }, []);
  return <>
      {/* Phones: his notes exactly like his first chat (upper right of him); desktop: beside him */}
      {on && (isMobile ? <QuestFinaleNotesMobile notes={notes} thinking={thinking} reduce={reduce} data-id="element-205" data-mp="12xhzw92db1qm" /> : <NotesBesideHim notes={notes} thinking={thinking} reduce={reduce} data-id="element-206" data-mp="ywek1mkbl5pe" />)}
      {!reduce && on && beat === 'rise' && <RocketExhaust data-id="element-207" data-mp="1cnt5s15m3l05" />}
    </>;
}

/** Page rumble strength 0..1 (CSS reads --quest-rumble); null switches it off */
function setRumble(strength: number | null) {
  const root = document.documentElement;
  if (strength === null) {
    root.classList.remove('quest-rumble');
    root.style.removeProperty('--quest-rumble');
    return;
  }
  root.classList.add('quest-rumble');
  root.style.setProperty('--quest-rumble', strength.toFixed(3));
}

/** Eases the rumble from one strength to another over `ms` (builds up with an ease-in) */
function rampRumble(from: number, to: number, ms: number, alive: () => boolean): Promise<void> {
  const start = performance.now();
  return new Promise(resolve => {
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / ms);
      setRumble(from + (to - from) * (to > from ? k * k : k));
      if (k < 1 && alive()) requestAnimationFrame(step);else resolve();
    };
    requestAnimationFrame(step);
  });
}

/**
 * His messages right next to him, wherever he is (the middle of the footer): beside him on wider
 * screens, above him on phones. Follows him every frame, so it stays with him as the page scrolls.
 */
function NotesBesideHim({
  notes,
  thinking,
  reduce
}: {
  notes: Note[];
  thinking: boolean;
  reduce: boolean;
}) {
  const right = useMotionValue(-9999);
  const bottom = useMotionValue(-9999);
  const [room, setRoom] = useState(360);
  const colRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    let lastRoom = 0;
    const loop = () => {
      const p = quest.pixelPx;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (p.x > -9000) {
        const narrow = vw < 768;
        // Keep the whole column on screen: never above the top, never below the bottom
        const h = colRef.current?.offsetHeight ?? 0;
        const clampBottom = (b: number) => Math.max(12, Math.min(b, vh - h - 12));
        let r: number;
        if (narrow) {
          // Above him, opening toward the upper left; the tail sits over his head
          const edge = Math.min(vw - 12, p.x + p.r * 0.6);
          right.set(vw - edge);
          bottom.set(clampBottom(vh - (p.y - p.r * 1.02)));
          // Only as wide as the space to his left allows, so nothing spills off the left edge
          r = Math.round(Math.min(420, Math.max(160, edge - 12)));
        } else {
          // To his left, tail pointing at him
          right.set(vw - (p.x - p.r * 0.95));
          bottom.set(clampBottom(vh - (p.y + p.r * 0.15)));
          r = Math.round(Math.min(420, Math.max(180, p.x - p.r * 0.95 - 24)));
        }
        if (Math.abs(r - lastRoom) > 8) {
          lastRoom = r;
          setRoom(r);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [right, bottom]);

  // Phones: fewer notes stacked at once (two on short screens), so they never crowd him out
  const narrow = typeof window !== 'undefined' && window.innerWidth < 768;
  const stack = narrow ? window.innerHeight < 700 ? 2 : 3 : MAX_STACK;
  const shown = notes.slice(-stack);
  return <div className="pointer-events-none fixed inset-0 z-[80]" aria-live="off" data-id="element-208" data-mp="3hnax5n5vfnja">
      <motion.div ref={colRef} className="absolute flex flex-col items-end gap-[8px]" style={{
      right,
      bottom,
      maxWidth: room
    }} data-id="element-209" data-mp="3loefcgbe5vno">
        <div role="log" aria-live="polite" aria-label="Pixel says" className="flex flex-col items-end gap-[6px]" data-id="element-210" data-mp="blv6p2znybhy">
          <AnimatePresence initial={false} data-id="element-211" data-mp="6knyj2tlzaar">
            {shown.map((n, i) => <DockNote key={n.id} text={n.text} tail={i === shown.length - 1} reduce={reduce} large maxWidth={room} data-id="element-212" data-mp="lcpwghc4bt85" data-mpi={i} />)}
          </AnimatePresence>
        </div>
        <div className="flex h-[34px] items-center justify-end" data-id="element-213" data-mp="ub1qktc7195c">
          <AnimatePresence data-id="element-214" data-mp="pd8n4829yt8r">
            {thinking && <motion.div key="think" className="rounded-full shadow-[0_2px_10px_rgba(0,0,0,0.12)]" exit={{
            opacity: 0,
            transition: {
              duration: 0.12
            }
          }} data-id="element-215" data-mp="pcqlevwufmtg">
                <ThinkingDots reduce={reduce} tail="bottom-right" data-id="element-216" data-mp="zawk74ncscu3" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>;
}

/**
 * Lift-off exhaust: a stream of glowing pixels pouring out under him, all in his interface red,
 * drawn on one canvas just below his layer. The plume grows as he picks up speed.
 */
function RocketExhaust() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    type P = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      max: number;
      s: number;
      c: string;
    };
    const parts: P[] = [];
    const start = performance.now();
    let last = start;
    let raf = 0;
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const k = Math.min(1, (now - start) / (LIFTOFF_S * 1000));
      const p = quest.pixelPx;
      if (p.x > -9000) {
        const n = Math.round(4 + k * 14);
        for (let i = 0; i < n; i++) {
          parts.push({
            x: p.x + (Math.random() - 0.5) * p.r * 0.5,
            y: p.y + p.r * 0.8,
            vx: (Math.random() - 0.5) * 160,
            vy: 380 + Math.random() * 520 + k * 900,
            life: 0,
            max: 0.35 + Math.random() * 0.45,
            s: 4 + Math.random() * (8 + k * 10),
            c: REDS[Math.floor(Math.random() * REDS.length)]
          });
        }
      }
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (let i = parts.length - 1; i >= 0; i--) {
        const q = parts[i];
        q.life += dt;
        if (q.life >= q.max) {
          parts.splice(i, 1);
          continue;
        }
        q.x += q.vx * dt;
        q.y += q.vy * dt;
        ctx.globalAlpha = 1 - q.life / q.max;
        ctx.fillStyle = q.c;
        ctx.fillRect(q.x - q.s / 2, q.y - q.s / 2, q.s, q.s);
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={ref} aria-hidden="true" className="pointer-events-none fixed inset-0 z-[64] h-full w-full" data-id="element-217" data-mp="2vk9ryp8cskjr" />;
}